import React, { useMemo } from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { compose, withProps } from 'recompose';

import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import user from 'js/lib/user';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import withAccomplishmentsandPaymentsRecommendationData from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationData';
import type { withAccomplishmentsAndPaymentsRecommendationDataProps } from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationData';
import type {
  AccomplishmentsPaymentsRecommendationQuery as AccomplishmentsPaymentsRecommendationQueryType,
  AccomplishmentsPaymentsRecommendationQueryVariables,
  AccomplishmentsPaymentsRecommendationQuery_CourseAccomplishmentsV1Resource_byUser_elements as CourseAccomplishments,
} from 'bundles/accomplishments/queries/__generated__/AccomplishmentsPaymentsRecommendationQuery';
import useCoursesByIds from 'bundles/course-v2/hooks/useCoursesByIds';
import { OnDemandSpecializationMembershipRole } from 'bundles/enroll-common/api/__generated__/globalTypes';
import type { S12nMemberships } from 'bundles/enroll-common/api/withS12nMembershipData';
import withS12nMembershipData from 'bundles/enroll-common/api/withS12nMembershipData';
import type { Specialization, Specializations } from 'bundles/s12n-common/api/withSpecializationsData';
import withSpecializationsData from 'bundles/s12n-common/api/withSpecializationsData';

type InputProps = {};
type GraphqlProps = {};
type GraphqlData = AccomplishmentsPaymentsRecommendationQueryType;
type GraphqlVariables = AccomplishmentsPaymentsRecommendationQueryVariables;
type GraphqlChildProps = {
  s12nData?: { s12nId: string; s12nName: string; s12nRemainingCourses: string[] }[];
  s12nUpNext?: string[][];
  recommendationDisplayPolicy?: string;
  s12nList: string[];
  completedCourseList: string[];
};

// This implements a switching logic among different Recommendation variations, which is expected to be used in A/B testings or user targeting
// Here, we generate an impression and switch among Recommendation variations.

export const DISPLAY_POLICY = {
  NO_COMPLETED_COURSES: 'no-completed-courses',
  COMPLETED_COURSE: 'completed-course',
};

const AccomplishmentsPaymentsRecommendationQuery = gql`
  query AccomplishmentsPaymentsRecommendationQuery($userId: String!) {
    CourseAccomplishmentsV1Resource {
      byUser(userId: $userId) {
        elements {
          test: id
          courseId
        }
      }
    }
  }
`;

export const isDayOneUser = (hasRec: boolean, hasCard?: boolean) => {
  return !!(hasRec || hasCard);
};

type CompletedCourses = CourseAccomplishments[];

type S12nEnrollments = {
  s12n: Specialization;
}[];

export const withRecommendationPolicySelector = withProps(
  (props: { completedCourses: CompletedCourses; s12nEnrollments: S12nEnrollments }) => {
    const { completedCourses, s12nEnrollments } = props;
    const hasCompleted = completedCourses.length > 0;
    let recommendationDisplayPolicy: string;
    const completedCourseList: string[] = [];

    let s12nList: string[] = [];
    const s12nData: {
      s12nId: string;
      s12nName: string;
      s12nRemainingCourses: string[];
    }[] = [];

    if (!hasCompleted) {
      recommendationDisplayPolicy = DISPLAY_POLICY.NO_COMPLETED_COURSES;
      return { recommendationDisplayPolicy, s12nList, s12nData, completedCourseList };
    }

    s12nEnrollments.forEach((specialization) => {
      const s12nId = specialization?.s12n?.id || '';
      const s12nName = specialization?.s12n?.name || '';
      const s12nRemainingCourses = specialization?.s12n?.courses.map((course) => course.id) || [];
      s12nList = s12nList.concat(s12nRemainingCourses);
      s12nData.push({
        s12nId,
        s12nName,
        s12nRemainingCourses,
      });
    });

    completedCourses.forEach((element) => {
      if (element) {
        completedCourseList.push(element.courseId);
      }
    });

    recommendationDisplayPolicy = DISPLAY_POLICY.COMPLETED_COURSE;
    return { recommendationDisplayPolicy, s12nList, s12nData, completedCourseList };
  }
);

type CourseInputProps = { s12nList: string[] };

// Type for course data formatted for accomplishments processing
type AccomplishmentsCourseData = {
  id: string;
  title: string;
  imageSrc: string;
  partners: {
    elements: {
      name: string;
      squareLogo: string;
    }[];
  };
};

type GraphqlCourseChildProps = { courseData?: AccomplishmentsCourseData[] };

// This determines a recommendation policy for a user
export const withRecommendationPolicy = compose<GraphqlChildProps, InputProps>(
  graphql<GraphqlProps, GraphqlData, GraphqlVariables, { completedCourses: CompletedCourses }>(
    AccomplishmentsPaymentsRecommendationQuery,
    {
      options: () => ({
        variables: { userId: `${user.get().id}` },
      }),
      props: ({ data }) => ({
        completedCourses:
          data?.CourseAccomplishmentsV1Resource?.byUser?.elements.flatMap((element) =>
            element !== null ? [element] : []
          ) ?? [],
      }),
    }
  )
);

// Hook-based course fetching to replace the GraphQL HOC
const upNextCourses = (Component: React.ComponentType<GraphqlCourseChildProps>) => {
  const Nextcourses = (props: CourseInputProps) => {
    const { s12nList = [] } = props;
    const { courseData: rawCourseData } = useCoursesByIds(s12nList);

    // Format course data for accomplishments processing
    const courseData = useMemo(() => {
      if (!rawCourseData) return undefined;

      return rawCourseData.map((course) => ({
        id: course.id,
        title: course.name,
        imageSrc: course.promoPhotoUrl || '',
        partners: {
          elements:
            course.partners?.map((partner) => ({
              name: partner.name,
              squareLogo: partner.squareLogo || '',
            })) || [],
        },
      }));
    }, [rawCourseData]);

    return <Component {...props} courseData={courseData} />;
  };

  return Nextcourses;
};

const withUserIdAndRole = withProps(() => ({
  userId: user.get().id,
  role: OnDemandSpecializationMembershipRole.LEARNER,
}));

const withS12nIds = withProps((props: { s12nMemberships: S12nMemberships }) => ({
  ...props,
  s12nIds: props.s12nMemberships.map((membership) => membership.s12nId),
}));

export const withCombinedS12nData = withProps(
  (props: { s12nMemberships: S12nMemberships; specializations: Specializations }) => {
    const s12nEnrollments: S12nEnrollments = [];
    const { s12nMemberships, specializations } = props;
    for (const membership of s12nMemberships) {
      const { s12nId } = membership;
      const specialization = specializations.find(({ id }) => id === s12nId);
      if (specialization) {
        s12nEnrollments.push({
          ...membership,
          s12n: specialization,
        });
      }
    }
    return { ...props, s12nEnrollments };
  }
);

export default (Component: React.ComponentType<withAccomplishmentsAndPaymentsRecommendationDataProps>) =>
  compose<withAccomplishmentsAndPaymentsRecommendationDataProps, InputProps>(
    deferToClientSideRender(),
    withUserIdAndRole,
    withS12nMembershipData,
    withS12nIds,
    withSpecializationsData,
    withCombinedS12nData,
    withRecommendationPolicy,
    withRecommendationPolicySelector,
    upNextCourses,
    withAccomplishmentsandPaymentsRecommendationData
  )(Component);
